/**
 * Preview multi-canal — el corazón de la herramienta de marketing del promotor.
 * Muestra cómo se verá el mensaje EN CADA CANAL antes de compartir, porque el
 * mismo texto con formato se ve distinto:
 *   - WhatsApp: *negrita* _cursiva_ ~tachado~ > cita  → los renderiza.
 *   - Telegram: igual sintaxis visual (la app la interpreta).
 *   - Redes (Instagram/Facebook): NO renderizan formato → los símbolos quedan
 *     como basura literal. Por eso para Redes se muestra/comparte TEXTO PLANO.
 *
 * Así el promotor ve la VERDAD de cada canal y no manda asteriscos feos a Instagram.
 *
 * El render usa un PARSER char-por-char que replica las reglas reales de WhatsApp
 * (no un regex codicioso) — los delimitadores desbalanceados/sobrantes quedan como
 * TEXTO LITERAL, no como formato roto. Ej: "*dinheiro*** *jogandor?*" → dinheiro
 * en negrita, ** literal, jogandor? en negrita (igual que WhatsApp).
 */

const DELIMS = { '*': 'b', '_': 'i', '~': 's' };
const TAGS = { b: 'strong', i: 'em', s: 'del' };

function escapar(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function esEspacio(ch) {
    return ch === ' ' || ch === '\n' || ch === '\t' || ch === undefined;
}
// Borde izquierdo del abridor: inicio, espacio, o puntuación NO-alfanumérica —
// PERO no el mismo delimitador (así `**` no arranca un span, queda literal).
function esBordeIzquierdo(ch, delim) {
    if (ch === undefined || ch === ' ' || ch === '\n' || ch === '\t') return true;
    if (ch === delim) return false; // ** / __ / ~~ no abren
    return !/[a-zA-Z0-9]/.test(ch);
}
// Borde derecho del cerrador: fin o char no-alfanumérico.
function esBordeDerecho(ch) {
    return ch === undefined || !/[a-zA-Z0-9]/.test(ch);
}

// Busca el índice del cerrador válido para un delimitador abierto en `apertura`.
// Devuelve el índice del cerrador, o -1 si no hay par válido.
function buscarCierre(texto, delim, apertura) {
    if (esEspacio(texto[apertura + 1])) return -1; // cara interna abridor no-espacio
    if (texto[apertura + 1] === delim) return -1; // ** no abre
    for (let i = apertura + 1; i < texto.length; i++) {
        if (texto[i] === '\n') return -1; // los spans inline no cruzan línea
        if (texto[i] !== delim) continue;
        if (esEspacio(texto[i - 1])) continue; // cara interna cerrador no-espacio
        if (texto[i + 1] === delim) continue; // *** no cierra acá (deja el doble literal)
        if (!esBordeDerecho(texto[i + 1])) continue; // borde derecho
        if (i === apertura + 1) continue; // contenido no vacío
        return i;
    }
    return -1;
}

// Parsea `texto` emitiendo tokens por callbacks. Reusado por render y plano.
function parsear(texto, onTexto, onFormato) {
    let buffer = '';
    const flush = () => {
        if (buffer) {
            onTexto(buffer);
            buffer = '';
        }
    };
    let i = 0;
    while (i < texto.length) {
        const ch = texto[i];
        if (DELIMS[ch] && esBordeIzquierdo(texto[i - 1], ch)) {
            const cierre = buscarCierre(texto, ch, i);
            if (cierre !== -1) {
                flush();
                onFormato(DELIMS[ch], texto.slice(i + 1, cierre));
                i = cierre + 1;
                continue;
            }
        }
        buffer += ch;
        i++;
    }
    flush();
}

/**
 * Render del texto a HTML con formato WhatsApp/Telegram (negrita/cursiva/tachado +
 * cita por línea). Parser fiel a WhatsApp; delimitadores sueltos quedan literales.
 * Escapa HTML (XSS). Para el preview de WhatsApp y Telegram.
 */
export function renderFormato(texto) {
    if (texto == null) return '';
    // Cita: línea que empieza con "> " → bloque citado. Se procesa por línea, y el
    // resto de la línea pasa por el parser inline.
    const lineas = String(texto).split('\n');
    return lineas
        .map(linea => {
            const cita = /^>\s?(.*)$/.exec(linea);
            if (cita) {
                return `<span class="pm-fmt-cita">${renderInline(cita[1])}</span>`;
            }
            return renderInline(linea);
        })
        .join('<br>');
}

function renderInline(texto) {
    let out = '';
    parsear(
        String(texto),
        t => {
            out += escapar(t);
        },
        (tag, inner) => {
            const g = TAGS[tag];
            out += `<${g}>${renderInline(inner)}</${g}>`; // recursivo: formato anidado
        }
    );
    return out;
}

/**
 * Texto PLANO: quita SOLO los delimitadores que forman par VÁLIDO, dejando los
 * asteriscos literales intactos. Para Instagram/Facebook (no renderizan formato).
 * Esta es la versión que se COMPARTE a Redes.
 */
export function aTextoPlano(texto) {
    if (texto == null) return '';
    const lineas = String(texto).split('\n');
    return lineas
        .map(linea => {
            const cita = /^>\s?(.*)$/.exec(linea);
            return planoInline(cita ? cita[1] : linea); // la cita pierde el "> "
        })
        .join('\n');
}

function planoInline(texto) {
    let out = '';
    parsear(
        String(texto),
        t => {
            out += t;
        },
        (_tag, inner) => {
            out += planoInline(inner); // recursivo: pela el anidado
        }
    );
    return out;
}

/** Render del texto plano (escapado) para el preview de Redes. */
function renderPlano(texto) {
    return escapar(aTextoPlano(texto)).replace(/\n/g, '<br>');
}

// Canales del preview: cada uno su render + clase de burbuja.
const CANALES = [
    { id: 'whatsapp', label: 'WhatsApp', render: renderFormato, burbuja: 'pm-bubble-wa' },
    { id: 'telegram', label: 'Telegram', render: renderFormato, burbuja: 'pm-bubble-tg' },
    { id: 'redes', label: 'Redes', render: renderPlano, burbuja: 'pm-bubble-redes' },
];

/**
 * Crea el preview multi-canal: tabs WhatsApp/Telegram/Redes + la burbuja del canal
 * activo, fiel a cómo se ve en esa app. Se actualiza con `actualizar(texto, link)`.
 * @returns {{ el:HTMLElement, actualizar:(texto:string, link:string)=>void }}
 */
export function crearPreviewMulticanal() {
    const el = document.createElement('div');
    el.className = 'pm-preview-mc';
    let canalActivo = 'whatsapp';
    let ultimoTexto = '';
    let ultimoLink = '';

    el.innerHTML = `
        <div class="pm-preview-tabs">
            ${CANALES.map(
                c => `<button type="button" class="pm-preview-tab ${c.id === canalActivo ? 'active' : ''}" data-canal="${c.id}">${c.label}</button>`
            ).join('')}
        </div>
        <div class="pm-preview-fondo" id="pm-preview-fondo"></div>`;

    const fondo = el.querySelector('#pm-preview-fondo');

    function pintar() {
        const canal = CANALES.find(c => c.id === canalActivo) || CANALES[0];
        const txt = (ultimoTexto || '').trim();
        const linkHtml = ultimoLink ? `<span class="pm-bubble-link">${escapar(ultimoLink)}</span>` : '';
        if (!txt && !ultimoLink) {
            fondo.innerHTML = `<div class="pm-preview-vacio"></div>`;
            return;
        }
        const cuerpo = txt ? canal.render(txt) : '';
        const sep = txt && ultimoLink ? '<br>' : '';
        fondo.className = 'pm-preview-fondo ' + canal.burbuja + '-fondo';
        fondo.innerHTML = `
            <div class="pm-bubble ${canal.burbuja}">
                <div class="pm-bubble-txt">${cuerpo}${sep}${linkHtml}</div>
                <div class="pm-bubble-meta">${horaActual()} <span class="pm-bubble-check">✓✓</span></div>
            </div>`;
    }

    el.querySelector('.pm-preview-tabs').addEventListener('click', e => {
        const tab = e.target.closest('[data-canal]');
        if (!tab) return;
        canalActivo = tab.dataset.canal;
        el.querySelectorAll('.pm-preview-tab').forEach(t => t.classList.toggle('active', t.dataset.canal === canalActivo));
        pintar();
    });

    function actualizar(texto, link) {
        ultimoTexto = texto || '';
        ultimoLink = link || '';
        pintar();
    }

    pintar();
    return { el, actualizar, getCanal: () => canalActivo };
}

// Hora HH:MM sin Date.now prohibido en otros contextos — acá es UI, ok usar Date.
function horaActual() {
    try {
        const d = new Date();
        const p = n => String(n).padStart(2, '0');
        return `${p(d.getHours())}:${p(d.getMinutes())}`;
    } catch (_) {
        return '';
    }
}
